import { modals } from "@mantine/modals";
import { Badge, Button, Image } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { Link } from "react-router-dom";
import DataTable from "../../components/common/DataTable";
import Head from "../../components/common/Head";
import { deleteLiveChannel } from "../../utils/api";
import { useLiveChannels } from "../../utils/queries";

const LiveChannels = () => {
  const [page, setPage] = React.useState(1);
  const limit = 10;
  const queryClient = useQueryClient();

  const { data, isLoading } = useLiveChannels(page, limit);
  const channels = data?.data || data || [];
  const total = data?.total || channels.length;

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteLiveChannel(id),
    onSuccess: () => {
      notifications.show({ message: "Live channel deleted successfully", color: "green" });
      queryClient.invalidateQueries(["live-channels"]);
    },
    onError: (error) => {
      notifications.show({ message: error?.response?.data?.message || "Failed to delete", color: "red" });
    },
  });

  const columns = [
    {
      title: "Logo",
      dataIndex: "logo",
      key: "logo",
      render: (text, record) => (
        <Image
          src={record.logo}
          alt={record.name}
          w={60}
          h={60}
          fit="cover"
          radius="md"
          fallbackSrc="https://placehold.co/60x60?text=No+Logo"
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Stream URL",
      dataIndex: "streamUrl",
      key: "streamUrl",
      render: (text) => (
        <span
          className="text-xs text-zinc-400 max-w-[200px] block truncate"
          title={text}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
    },
    {
      title: "Status",
      dataIndex: "isEnable",
      key: "isEnable",
      render: (text, record) => (
        <Badge color={record.isEnable ? "green" : "red"} variant="light">
          {record.isEnable ? "Enabled" : "Disabled"}
        </Badge>
      ),
    },
    {
      title: "Actions",
      dataIndex: "_id",
      key: "action",
      render: (text, record) => (
        <div className="flex gap-2 flex-wrap">
          <Link to={`/live-channels/edit/${record._id}`}>
            <Button variant="light" size="xs">Edit</Button>
          </Link>
          <Button
            color="red"
            variant="light"
            size="xs"
            onClick={() => {
              modals.openConfirmModal({
                title: "Delete Live Channel",
                children: "This action cannot be undone.",
                labels: { confirm: "Delete", cancel: "Cancel" },
                confirmProps: { color: "red" },
                onConfirm: () => deleteMutation.mutate(record._id),
              });
            }}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="table-page">
      <Head
        title="Live Channels"
        customButton={
          <Link to="/live-channels/add">
            <Button size="sm">Add Live Channel</Button>
          </Link>
        }
      />
      <DataTable
        scroll={{ x: 800 }}
        dataSource={channels}
        columns={columns}
        loading={isLoading || deleteMutation.isPending}
        pagination={
          total > limit
            ? { total, page, onChange: setPage, pageSize: limit }
            : false
        }
      />
    </div>
  );
};

export default LiveChannels;
